import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { inviteAPI } from '../../services/api';
import { FiCopy, FiCheck, FiTrash2, FiArrowLeft } from 'react-icons/fi';
import '../Dashboard.css';

const GenerateInvite = () => {
  const [formData, setFormData] = useState({
    mobile_number: '',
    role: 'student'
  });
  const [invites, setInvites] = useState([]);
  const [generatedLink, setGeneratedLink] = useState('');
  const [copiedId, setCopiedId] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('all'); // all, active, used, expired
  const navigate = useNavigate();

  useEffect(() => {
    loadInvites();
  }, []);

  const loadInvites = async () => {
    try {
      const response = await inviteAPI.getAllInvites();
      setInvites(response.data);
    } catch (err) {
      console.error('Error loading invites:', err);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError('');
  };

  const handleGenerate = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    // Validate mobile number
    if (!/^[0-9]{10}$/.test(formData.mobile_number)) {
      setError('Mobile number must be exactly 10 digits');
      setLoading(false);
      return;
    }

    try {
      const response = await inviteAPI.generateInvite(formData);
      setGeneratedLink(response.data.invite.invite_link);
      setSuccess('Invitation generated successfully!');
      setFormData({ mobile_number: '', role: 'student' });
      loadInvites();
    } catch (err) {
      const errorMessage = err.response?.data?.error || 'Failed to generate invitation';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (link, id) => {
    navigator.clipboard.writeText(link);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const handleRevoke = async (id) => {
    if (!window.confirm('Are you sure you want to revoke this invitation?')) {
      return;
    }

    try {
      await inviteAPI.revokeInvite(id);
      setSuccess('Invitation revoked successfully');
      loadInvites();
    } catch (err) {
      setError('Failed to revoke invitation');
    }
  };

  const getInviteStatus = (invite) => {
    if (invite.is_used) return 'used';
    if (new Date(invite.expires_at) < new Date()) return 'expired';
    return 'active';
  };

  const filteredInvites = invites.filter(invite => {
    if (filter === 'all') return true;
    return getInviteStatus(invite) === filter;
  });

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    const badges = {
      active: { class: 'badge-open', text: 'Active' },
      used: { class: 'badge-resolved', text: 'Used' },
      expired: { class: 'badge-closed', text: 'Expired' }
    };
    return badges[status] || badges.active;
  };

  return (
    <div className="dashboard-container">
      <header className="dashboard-header">
        <div className="container">
          <div className="header-content">
            <div className="header-left">
              <button
                onClick={() => navigate('/dashboard')}
                className="btn btn-secondary"
                style={{ marginRight: '16px' }}
              >
                <FiArrowLeft /> Back
              </button>
              <div>
                <h1 className="dashboard-title">Generate Invitation Links</h1>
                <p className="dashboard-subtitle">Create invite-only registration links</p>
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="dashboard-content container" style={{ maxWidth: '900px' }}>
        {/* Generate Form */}
        <div className="ticket-detail-card" style={{ marginBottom: '24px' }}>
          <h2 style={{ marginBottom: '16px', fontSize: '20px' }}>Generate New Invitation</h2>

          {error && <div className="error-message">{error}</div>}
          {success && <div className="info-message" style={{ background: 'rgba(16, 185, 129, 0.1)', borderColor: 'rgba(16, 185, 129, 0.3)', marginBottom: '16px' }}>{success}</div>}

          <form onSubmit={handleGenerate} style={{ marginBottom: '24px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr auto', gap: '12px', alignItems: 'end' }}>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">Mobile Number (10 digits) *</label>
                <input
                  type="tel"
                  name="mobile_number"
                  className="form-input"
                  value={formData.mobile_number}
                  onChange={handleChange}
                  required
                  pattern="[0-9]{10}"
                  maxLength="10"
                  placeholder="9876543210"
                />
              </div>

              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">Role *</label>
                <select
                  name="role"
                  className="form-input"
                  value={formData.role}
                  onChange={handleChange}
                  style={{ cursor: 'pointer' }}
                >
                  <option value="student">Student</option>
                  <option value="staff">Staff</option>
                  <option value="admin">Admin</option>
                </select>
              </div>

              <button
                type="submit"
                className="btn btn-primary"
                disabled={loading}
                style={{ height: '42px' }}
              >
                {loading ? 'Generating...' : 'Generate Invite'}
              </button>
            </div>
          </form>

          {generatedLink && (
            <div style={{
              background: 'rgba(59, 130, 246, 0.1)',
              border: '2px solid rgba(59, 130, 246, 0.3)',
              borderRadius: '8px',
              padding: '16px'
            }}>
              <div style={{ marginBottom: '8px', fontWeight: '600' }}>Generated Invitation Link:</div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <input
                  type="text"
                  value={generatedLink}
                  readOnly
                  className="form-input"
                  style={{ flex: 1, fontSize: '13px' }}
                />
                <button
                  onClick={() => copyToClipboard(generatedLink, 'new')}
                  className="btn btn-secondary"
                  style={{ whiteSpace: 'nowrap' }}
                >
                  {copiedId === 'new' ? <><FiCheck /> Copied!</> : <><FiCopy /> Copy</>}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Invites List */}
        <div className="ticket-detail-card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <h2 style={{ fontSize: '20px', margin: 0 }}>All Invitations ({filteredInvites.length})</h2>
            <div style={{ display: 'flex', gap: '8px' }}>
              {['all', 'active', 'used', 'expired'].map(f => (
                <button
                  key={f}
                  onClick={() => setFilter(f)}
                  className={`btn ${filter === f ? 'btn-primary' : 'btn-secondary'}`}
                  style={{ fontSize: '14px', padding: '6px 12px' }}
                >
                  {f.charAt(0).toUpperCase() + f.slice(1)}
                </button>
              ))}
            </div>
          </div>

          {filteredInvites.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px 20px', color: 'var(--text-secondary)' }}>
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>📧</div>
              <p>No {filter !== 'all' ? filter : ''} invitations found</p>
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid var(--border)' }}>
                    <th style={{ padding: '12px', textAlign: 'left', fontSize: '14px' }}>Mobile Number</th>
                    <th style={{ padding: '12px', textAlign: 'left', fontSize: '14px' }}>Role</th>
                    <th style={{ padding: '12px', textAlign: 'left', fontSize: '14px' }}>Status</th>
                    <th style={{ padding: '12px', textAlign: 'left', fontSize: '14px' }}>Created</th>
                    <th style={{ padding: '12px', textAlign: 'left', fontSize: '14px' }}>Expires</th>
                    <th style={{ padding: '12px', textAlign: 'left', fontSize: '14px' }}>Registered User</th>
                    <th style={{ padding: '12px', textAlign: 'right', fontSize: '14px' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredInvites.map(invite => {
                    const status = getInviteStatus(invite);
                    const badge = getStatusBadge(status);
                    const inviteLink = `${process.env.REACT_APP_SERVER_URL || window.location.origin}/register?token=${invite.invite_token}`;

                    return (
                      <tr key={invite.id} style={{ borderBottom: '1px solid var(--border)' }}>
                        <td style={{ padding: '12px', fontWeight: '500' }}>{invite.mobile_number}</td>
                        <td style={{ padding: '12px' }}>
                          <span style={{
                            padding: '4px 8px',
                            borderRadius: '4px',
                            fontSize: '12px',
                            background: 'var(--bg-secondary)'
                          }}>
                            {invite.target_role}
                          </span>
                        </td>
                        <td style={{ padding: '12px' }}>
                          <span className={`badge ${badge.class}`}>{badge.text}</span>
                        </td>
                        <td style={{ padding: '12px', fontSize: '13px', color: 'var(--text-secondary)' }}>
                          {formatDate(invite.created_at)}
                        </td>
                        <td style={{ padding: '12px', fontSize: '13px', color: 'var(--text-secondary)' }}>
                          {formatDate(invite.expires_at)}
                        </td>
                        <td style={{ padding: '12px', fontSize: '13px' }}>
                          {invite.registered_user_name || '-'}
                        </td>
                        <td style={{ padding: '12px', textAlign: 'right' }}>
                          <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                            {status === 'active' && (
                              <>
                                <button
                                  onClick={() => copyToClipboard(inviteLink, invite.id)}
                                  className="btn btn-secondary"
                                  style={{ fontSize: '12px', padding: '6px 12px' }}
                                  title="Copy invite link"
                                >
                                  {copiedId === invite.id ? <FiCheck /> : <FiCopy />}
                                </button>
                                <button
                                  onClick={() => handleRevoke(invite.id)}
                                  className="btn btn-secondary"
                                  style={{ fontSize: '12px', padding: '6px 12px', color: '#ef4444' }}
                                  title="Revoke invite"
                                >
                                  <FiTrash2 />
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default GenerateInvite;
